import {
	Component,
	ElementRef,
	OnDestroy,
	signal,
	Signal,
	computed,
	ViewChild,
	CUSTOM_ELEMENTS_SCHEMA,
	inject,
	effect,
	HostBinding, Inject
} from '@angular/core';
import { trigger, style, animate, transition, query, stagger } from '@angular/animations';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {ReactiveFormsModule} from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import {find, isArray, get} from 'lodash';
import {CoreModule} from "../../_core/core.module";
import { AppService } from '../../app.service';
import { OrderService } from 'src/app/_services/order.service';
import { ChatService, TranslateChatPipe } from '../chat.service';
import { ChatBasketService } from '../chat-basket.service';
import {OfferComponent} from "../../order-items/offer/offer.component";
import { OfferAttributesComponent } from "src/app/components/offer-attributes/offer-attributes.component";
import { OfferMarketingComponent } from "src/app/components/offer-marketing/offer-marketing.component";
import { LottieAnimationComponent } from "../../components/lottie-animation/lottie-animation.component";
import { ChatSummaryBottomSheetComponent } from "../chat-summary-bottom-sheet/chat-summary-bottom-sheet.component";
import { ChatSearchQuestionComponent } from "./chat-search-question/chat-search-question.component";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { InlineTranslationPickerComponent } from "../../components/inline-translation-picker/inline-translation-picker.component";

@Component({
	selector: 'app-chat-search-dialog',
	standalone: true,
	imports: [
		CoreModule,
		MatIcon,
		ReactiveFormsModule,
		OfferMarketingComponent,
		OfferAttributesComponent,
		ChatSearchQuestionComponent,
		LottieAnimationComponent,
		TranslateChatPipe,
		InlineTranslationPickerComponent
	],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	templateUrl: './chat-search-dialog.component.html',
	styleUrl: './chat-search-dialog.component.scss',
	animations: [
		trigger('listAnimation', [
			transition('* => *', [ // Any state change
				query(':enter', [ // Query for elements entering the DOM
					style({ opacity: 0, transform: 'translateY(-20px)' }),
					stagger(100, [ // Stagger the animations by 100ms
						animate('0.3s ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
					])
				], { optional: true }),
				query(':leave', [ // Query for elements leaving the DOM
					stagger(100, [
						animate('0.3s ease-in', style({ opacity: 0, transform: 'translateY(-20px)' }))
					])
				], { optional: true })
			])
		]),
		trigger('stagger', [
			transition('* => *', [
				query(':enter', [
						style({ opacity: 0, transform: 'scale(0.3) translateY(45px)' }),
						stagger(200, [
							animate('400ms ease-in', style({ opacity: 0.7, transform: 'scale(1) translateY(-60px)' })),
							animate('200ms ease-out', style({ opacity: 1, transform: 'scale(1) translateY(0)' })),

						])
					], { optional: true }
				)
			])
		])
	]
})
export class ChatSearchDialogComponent implements OnDestroy {
	@ViewChild('chatInput') private chatInput!: ElementRef<HTMLTextAreaElement>;
	@ViewChild('loadingAnim', { static: false }) public loadingComponent: LottieAnimationComponent;
	$storage: any;
	total: number = 0;
	offers: any[] = [];
	private previousSessionId = 'new';
	loading: Signal<boolean> = computed(() => {
		return this.chatService.loading();
	});
	readonly prompt = signal('');
	searchMode = signal('simple');
	processingMessage = signal(false);

	aiMessages = signal([]);
	endMessage = signal(null);

	lastSearchTerm = signal(null);
	selectedOffers = signal([]);

	isMealSuggestion = signal(false);
	wasBasketChange = signal(false);

	showAddAllOffers: Signal<boolean> = computed(() => {
		return !this.chatService.loading() && this.isMealSuggestion() && !this.wasBasketChange() && this.aiMessages()[0];
	});
	translationSubscription;
	activeQuestion: any = null;


	constructor(
		public dialog: MatDialog,
		private bottomSheet: MatBottomSheet,
		public appService: AppService,
		public dialogRef: MatDialogRef<ChatSearchDialogComponent>,
		public chatService: ChatService,
		public chatBasket: ChatBasketService,
		@Inject(MAT_DIALOG_DATA) public data: any,
		public orderService: OrderService,
	) {
		this.$storage = this.orderService.$storage;
		this.offers = this.orderService.getCatalogItemsAsFlatArray();

		if (this.$storage.cacheSearchResults) {
			const cacheParams = this.$storage.cacheSearchResults;
			this.searchMode.set('advanced');
			this.aiMessages.set(cacheParams.aiMessages);
			this.endMessage.set(cacheParams.endMessage);
			this.lastSearchTerm.set(cacheParams.lastSearchTerm);
		}

		this.chatService.initChatbot().then(res => {
			setTimeout(() => {this.loadingComponent.stop()}, 1500);
		});
		effect(() => {
			const currentSessionId = this.chatService.getSessionId();
			if (this.previousSessionId === 'new' && currentSessionId !== 'new') {
				this.setChatActive();
			}
			this.previousSessionId = currentSessionId;
		});

		const currentDir = this.appService.aiLocale.direction;
		this.translationSubscription = this.appService.translation$.subscribe(newLocal => {
			if (currentDir !== this.appService.aiLocale.direction) {
				this.dialogRef.close({reload: true});
			}
		});
		if (this.$storage?.order?.quantity){
			this.wasBasketChange.set(true);
		}

	}

	ngOnDestroy() {
		this.$storage.cacheSearchResults = null;
		this.translationSubscription.unsubscribe();
		if (this.searchMode() === 'advanced' && this.aiMessages().length) {
			this.$storage.cacheSearchResults = {
				endMessage: this.endMessage(),
				aiMessages: this.aiMessages(),
				lastSearchTerm: this.lastSearchTerm()
			}
		}
	}

	async setChatActive() {
		const currentSessionId = this.chatService.getSessionId();
		if (currentSessionId !== 'new') {

		}
	}

	refreshSearch() {
		if (this.searchMode() === 'advanced') {
			this.clearAdvancedSearch();
		} else {
			this.prompt.set('');
		}
	}

	cancel() {
		this.dialogRef.close();
	}

	selectQuestion(question: any) {
		if (this.activeQuestion === question) {
			this.activeQuestion = null;
		} else {
			this.activeQuestion = question;
		}

	}
	onQuestionApply(ev) {
		const q = ev;
		let translation = this.chatService.translateKey(q.searchPattern);
		q.fields.forEach((field: any) => {
			let value = field.value;
			if (isArray(value)) {
				value = value.map(v => this.chatService.translateKey(v)).join(', ');
			}
			translation = translation.replace(`{{${field.id}}}`, value);
		});
		this.activeQuestion = null;
		this.sendMessage(translation);
	}

	clearAdvancedSearch() {
		this.searchMode.set('simple');
		this.endMessage.set(null);
		this.aiMessages.set([]);
		this.cancelOngoingRequest();
	}

	searchTimeout;
	showStagger = true;
	searchByText(query: string) {
		clearTimeout(this.searchTimeout);
		this.showStagger = false;
		this.searchMode.set('simple');
		if (!query || query.length <= 2) {
			this.selectedOffers.set([]);
			return;
		}
		const searchString = query.toLowerCase();
		this.searchTimeout = setTimeout(() => {
			this.showStagger = true;
			const filteredOffers = this.offers.filter(offer => {
				if (offer.name.toLowerCase().includes(searchString)) return true;
				if (offer.description && offer.description.toLowerCase().includes(searchString)) return true;
			});
			this.selectedOffers.set(filteredOffers);
		}, 200);
	}

	menuItemClick(offer, ev) {
		this.dialogRef.close({
			offer: offer,
			ev: ev
		});
	}

	addItemsToBasket() {
		let payload = {
			validItems: [],
			invalidItems: [],
			partialItems: [],
			errorItems: [],
			addedItems: []
		}
		this.aiMessages().forEach(aiOffer => {
			try {
				if (aiOffer.type !== 'offer') return;
				const result = this.chatBasket.processSystemOrderItem(aiOffer.data, aiOffer.viewItem);
				result.quantity = aiOffer.data.quantity;
				if (result.success) {
					if (!result.hasValidationIssues) {
						result.basketOffer.quantity = aiOffer.data.quantity;
						this.orderService.addToBasket(result.basketOffer);
						payload.addedItems.push(result);
					} else {
						payload.partialItems.push(result);
					}
				} else {
					payload.invalidItems.push(result);
				}
			} catch (error) {
				console.error(`Error processing order item "${aiOffer.data.offerId}":`, error);
				payload.errorItems.push(aiOffer.viewItem);
			}
		});

		if (payload.partialItems.length > 0) {
			let bottomSheetRef = this.bottomSheet.open(ChatSummaryBottomSheetComponent, {
				data: {
					validItems: payload.partialItems,
					message: this.appService.translate('tabot.offers_need_input')
				},
				direction: this.appService.direction,
				panelClass: "bottom-sheet-summary",
				disableClose: true,
			});
			bottomSheetRef.afterDismissed().subscribe(result => {
				if (result) this.dialogRef.close({action: 'checkout'});
			});
		} else if (payload.addedItems.length > 0) {
			this.dialogRef.close({action: 'checkout'});
		}
	}

	//------------------------------------------------------------------------------------->
	//------------------------------------------------------------------------------------->
	//------------------------------------------------------------------------------------->

	async sendMessage(message?): Promise<any> {

		if (this.chatService.loading()) return;
		if (!message) message = this.prompt().trim();
		if (!message.length) return;
		this.searchMode.set('advanced');
		this.prompt.set('');
		this.processingMessage.set(true);
		this.adjustTextareaHeight();
		let that = this;
		try {
			this.chatService.cancelController.abort('Cancelling Controller');
		} catch (error) {
			console.debug('AbortController already aborted:', error);
		}
		this.chatService.cancelController = new AbortController();
		this.loadingComponent.play();
		this.chatService.loading.set(true);
		this.aiMessages.set([{
			type: 'question',
			info: message

		}]);
		this.isMealSuggestion.set(false);
		this.endMessage.set(null);
		let fullText = '';

		try {
			const stream = await this.chatService.sendMessageStream(
				message,
				this.chatService.cancelController.signal
			);
			const reader = stream.getReader();
			const decoder = new TextDecoder('utf-8');
			let lastParseTime = Date.now();
			while (true) {
				const { done, value } = await reader.read();
				if (done) break;
				const chunk = decoder.decode(value, { stream: true });
				fullText += chunk;
				if (Date.now() - lastParseTime > 100) {
					parseMessages(fullText);
					lastParseTime = Date.now();
				}
			}
			parseMessages(fullText);

		} catch (err: any) {
			console.error('Chat response error:', err);
		} finally {
			const messages = this.aiMessages();
			if (!message.length) {
				that.aiMessages.update(current => [
					{ data:{info: this.chatService.translateKey('No results found')}, type: 'info', _id: this.appService.getObjectId() }
				]);
			} else {
				if (messages.find(m => m.type === 'offer' && m.data.dinerName)) {
					this.isMealSuggestion.set(true);
				}
			}
			this.chatService.loading.set(false);
			this.processingMessage.set(false);
			this.chatService.cancelController = null;
			this.loadingComponent.stop();
		}

		function parseMessages(fullText) {
			const tempOffers = that.getResponseMessages(fullText, that.aiMessages());
			if (tempOffers) {
				console.log(tempOffers);
				that.aiMessages.update(current => tempOffers);
			}
		}
	}

	getResponseMessages(content, parseMessages) {
		let newMessages = [];
		content.replace(/@{(.*?)}@/sg, (match, content) => {
			const found = parseMessages.find(o => o.match === match);
			if (found) return '';
			let response = '';
			try {
				let json = match.slice(1, -1);
				const data = JSON.parse(json);
				const offerId = data.offerId;
				if (data.intro) {
					newMessages.push({data, match, _new: true, type: 'intro', _id: this.appService.getObjectId() });
				} else if (data.recommendationReason) {
					newMessages.push({data, match, _new: true, type: 'recommendationReason', _id: this.appService.getObjectId() });
				} else if (data.offTopicResponse) {
					newMessages.push({data, match, _new: true, type: 'offTopicResponse', _id: this.appService.getObjectId() });
				} else if (data.offerId) { // && data.quantity
					const viewItem = find(this.orderService.catalogItemsAsFlatArray, { offer: offerId });
					if (viewItem) {
						newMessages.push({viewItem, data, match, _new: true, type: 'offer', _id: this.appService.getObjectId() });
					} else {
						newMessages.push({data, match, _new: true, type: 'message', title: 'tabot.offer_not_found_in_catalog', _class: "alert-danger", _id: this.appService.getObjectId() });
					}
				} else if (data.dinerName) {
					newMessages.push({data, match, _new: true, type: 'diner', _id: this.appService.getObjectId() });
				} else if (data.info) {
					newMessages.push({data, match, _new: true, type: 'info', _id: this.appService.getObjectId() });
				} else {
					newMessages.push({data, match, _new: true, type: 'junk', _id: this.appService.getObjectId() });
				}
			} catch (e) {
				console.error('Error parsing offer patter:', content);
			}
			return response; // Keep original pattern if no image found
		});
		if (newMessages.length > 0) {
			parseMessages.forEach(offer => {
				delete offer._new;
			});
			return parseMessages.concat(newMessages);
		}
	}

	cancelOngoingRequest() {
		this.chatService.cancelOngoingRequest();
	}

	aiOfferClick(o, ev) {
		const result = this.chatBasket.processSystemOrderItem(o.data, o.viewItem);
		if (result.success) {
			this.selectOffer(result.basketOffer);
		}
	}

	async selectOffer(_offer) {
		if (_offer._disabled) {
			this.appService.alert({ content: this.appService.translate("ITEM_OUT_OF_STOCK_MESSAGE", { count: _offer.stock }) });
			return;
		}
		await this.orderService.validateOrderLimit(1, _offer.price);

		let dialogRef = this.dialog.open(OfferComponent, {
			panelClass: ['to-offer-dialog', 'animated', 'fast', 'slideInLeft'],
			backdropClass: 'kiosk-fullscreen-backdrop',
			direction: this.appService.direction,
			//disableClose: true,
			closeOnNavigation: true,
			data: {
				offer: _offer,
				isEdit: false,
				minWidth: 280
			},
			autoFocus: false
		});
		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				this.wasBasketChange.set(true);
				this.orderService.addToBasket(result.viewItem);
				this.chatService.updateBasket();
			}
		});

	}

	/**
	 * Handles keydown events for textarea
	 */
	onTextareaKeydown(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			// On mobile, Enter adds a new line (default behavior)
			// Only send message if Shift+Enter is pressed
			if (event.shiftKey || this.appService.isDesktop()) {
				event.preventDefault();
				this.sendMessage();
			}
		}
	}
	onTextareaKeyup(event: KeyboardEvent) {
		if (this.searchMode() !== 'advanced' && event.key !== 'Enter' && event.key !== 'Shift') {
			const message = this.prompt().trim();
			this.searchByText(message);
		}
	}

	checkout() {
		this.orderService.triggerCheckout();
	}

	adjustTextareaHeight(): void {
		const textarea = this.chatInput?.nativeElement;
		if (!textarea) return;

		// Reset height to calculate scrollHeight correctly
		textarea.style.height = 'auto';

		// Force reflow to ensure scrollHeight is recalculated
		textarea.offsetHeight;

		// Use the CSS values directly in pixels
		const minHeightPx = 40; // 2.5rem ≈ 40px (matches CSS min-height)
		const maxHeightPx = 96; // 6rem = 96px (matches CSS max-height)

		// Set height based on content, constrained to min/max
		const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeightPx), maxHeightPx);
		textarea.style.height = `${newHeight}px`;

		// Show/hide scrollbar when content exceeds max height
		textarea.style.overflowY = textarea.scrollHeight > maxHeightPx ? 'auto' : 'hidden';
	}

}
